import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, MessageCircle, Music, Video, Palette, Megaphone } from "lucide-react";
import Reveal from "../components/Reveal";
import SEO from "../components/SEO";
import api from "../lib/api";
import { useSite } from "../context/SiteContext";

const DELIVERABLES = [
    "Studio-quality song production", "Professional mixing & mastering", "Cover artwork & artist branding",
    "Music video or cinematic visualizer", "Social media creatives & reels", "Release strategy & marketing plan",
];
const STEPS = [
    ["01", "Discovery", "We understand your sound, goals and vision."],
    ["02", "Production", "We craft the record, visuals and brand assets."],
    ["03", "Release", "We plan and execute a release that gets attention."],
    ["04", "Grow", "We help you build momentum and a real audience."],
];

export default function Launch() {
    const { openLead, openWhatsApp } = useSite();
    const [tests, setTests] = useState([]);
    useEffect(() => { api.get("/testimonials").then((r) => setTests(r.data)).catch(() => {}); }, []);

    return (
        <div data-testid="launch-page">
            <SEO title="Artist Launch, Complete Release Package" description="MUVIARTS Artist Launch: production, visuals, branding and marketing to release your music professionally." />
            <section className="relative bg-espresso text-white overflow-hidden">
                <img loading="lazy" decoding="async" src="https://images.pexels.com/photos/15208427/pexels-photo-15208427.jpeg?auto=compress&w=2000" alt="Artist Launch" className="absolute inset-0 h-full w-full object-cover opacity-40" />
                <div className="absolute inset-0 bg-espresso/55" />
                <div className="relative mx-container py-20 lg:py-24 text-center">
                    <p className="overline !text-brand-gold mb-4">MUVIARTS Artist Launch</p>
                    <h1 className="font-display font-semibold text-5xl sm:text-7xl leading-[0.98] max-w-3xl mx-auto">Release your music <span className="italic text-brand-gold">like a professional.</span></h1>
                    <p className="mt-6 max-w-xl mx-auto text-white/70 text-lg">One complete package, production, visuals, branding and marketing, so you can focus on the art while we handle the launch.</p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                        <button onClick={() => openLead?.("Artist Launch")} className="btn-primary" data-testid="launch-start">Start My Artist Launch <ArrowRight className="h-4 w-4" /></button>
                        <button onClick={() => openWhatsApp?.("launch")} className="btn w-full sm:w-auto bg-[#25D366] text-white px-7 py-4" data-testid="launch-whatsapp"><MessageCircle className="h-4 w-4" /> WhatsApp us</button>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24 bg-white">
                <div className="mx-container grid lg:grid-cols-2 gap-12 items-start">
                    <div>
                        <p className="overline mb-3">Everything included</p>
                        <h2 className="font-display font-semibold text-4xl">A complete release, handled end-to-end.</h2>
                        <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                            {DELIVERABLES.map((d) => (<li key={d} className="flex items-start gap-2.5 text-neutral-700"><Check className="h-5 w-5 text-brand-gold shrink-0" /> {d}</li>))}
                        </ul>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        {[Music, Video, Palette, Megaphone].map((Icon, i) => (
                            <div key={i} className="bg-cream border border-[#e8e1d4] p-6"><Icon className="h-6 w-6 text-brand-golddark" /><p className="mt-3 font-display font-semibold">{["Production", "Visuals", "Branding", "Marketing"][i]}</p></div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24 bg-cream border-y border-[#e8e1d4]">
                <div className="mx-container">
                    <div className="text-center max-w-2xl mx-auto mb-10">
                        <p className="overline mb-3">The process</p>
                        <h2 className="font-display font-semibold text-4xl">From idea to release in four steps.</h2>
                    </div>
                    <div className="grid md:grid-cols-4 gap-6 text-center md:text-left">
                        {STEPS.map(([n, t, d]) => (<div key={n}><span className="big-index text-4xl text-[#d8ccb4]">{n}</span><h3 className="mt-3 font-display font-semibold text-xl">{t}</h3><p className="mt-2 text-sm text-neutral-500">{d}</p></div>))}
                    </div>
                </div>
            </section>

            {tests.length > 0 && <Testimonials tests={tests} />}

            <section className="py-20 bg-white">
                <div className="mx-container text-center">
                    <h2 className="font-display font-semibold text-4xl sm:text-5xl">Ready to build your release?</h2>
                    <p className="mt-4 text-neutral-500">Tell us about your project, we'll reply within one business day.</p>
                    <button onClick={() => openLead?.("Artist Launch")} className="btn-primary mt-8" data-testid="launch-cta-bottom">Build My Release <ArrowRight className="h-4 w-4" /></button>
                </div>
            </section>
        </div>
    );
}

function Testimonials({ tests }) {
    return (
        <section className="py-16 lg:py-24 bg-white" data-testid="testimonials">
            <div className="mx-container">
                <p className="overline mb-3 text-center">What artists say</p>
                <div className="grid md:grid-cols-3 gap-6">
                    {tests.slice(0, 3).map((t, i) => (
                        <div key={i} className="bg-cream border border-[#e8e1d4] p-6 text-center">
                            <p className="text-neutral-700 leading-relaxed">"{t.testimonial}"</p>
                            <div className="mt-4 flex items-center justify-center gap-3">
                                {t.image && <img loading="lazy" decoding="async" src={t.image} alt={t.name} className="h-10 w-10 rounded-full object-cover" />}
                                <div><p className="font-semibold text-sm">{t.name}</p><p className="text-xs text-neutral-400">{t.service}</p></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export { Testimonials };
